import React, { useEffect } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { Route, BrowserRouter, Routes } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { Amplify } from 'aws-amplify'
import { Box, CssBaseline } from '@mui/material'
import Login from './features/userauth/login/LogIn/log-in'
import ConfirmInvite from './features/userauth/signup/ConfirmInvite/confirm-invite'
import ProfileSetup from './features/userauth/signup/ProfileSetup/profile-setup'
import ResetPassword from './features/userauth/forgotpassword/ResetPassword/reset-password'
import ResetPasswordSent from './features/userauth/forgotpassword/ResetPasswordSent/reset-password-sent'
import SetNewPassword from './features/userauth/forgotpassword/SetNewPassword/set-new-password'
import SetNewPasswordSuccess from './features/userauth/forgotpassword/SetNewPasswordSuccess/set-new-password-success'
import UserManagement from './features/UserManagement/user-management'
import Dashboard from './features/Dashboard/dashboard'
import AccountSetting from './features/AccountSetting/account-setting'
import awsconfig from './aws-exports'
import Orders from './features/outboundorders/Orders/orders'
import OrderList from './features/outboundorders/OrderList/order-list'
import OrderDetails from './features/outboundorders/OrderDetails/order-details'
import Reports from './features/reporting/reports/Reports'
import DisplayReports from './features/reporting/displayreports/display-reports'
import PrivateRoutes from './features/userauth/PrivateRoutes/private-routes'
import { setAuthenticatedUser } from './features/userauth/login/LogIn/log-in-slice'
import { getAuthenticatedUserInfo } from './api/userauthentication/get-authenticated-user'
import Inventory from './features/inventory/Inventory/inventory'
import ItemDetails from './features/inventory/ItemDetails/item-details'
import ItemCodeSummary from './features/inventory/ItemCodeSummary/item-code-summary'
import SerialNumbers from './features/SerialNumbers/serial-numbers'
import OrderAllocations from './features/inventory/OrderAllocations/order-allocations'
import ItemSpecialPricing from './features/inventory/ItemSpecialPricing/item-special-pricing'
import CustomerGroup from './features/inventory/CustomerGroup/customer-group'
import Invoices from './features/invoice/Invoices/invoices'
import CreditInvoices from './features/invoice/CreditInvoices/credit-invoices'
import FallbackComponent from './components/FallbackComponent/fallback-component'
import ResendPasswordSuccess from './features/userauth/signup/ResendPasswordSuccess/resend-password-success'
import ResendPassword from './features/userauth/signup/ResendPassword/resend-password'
import ResendPasswordOutlet from './features/userauth/signup/ResendPasswordOutlet/resend-password-outlet'
import SignUpCompleted from './features/userauth/signup/SignUpCompleted/sign-up-completed'
import TemporaryPasswordExpired from './features/userauth/signup/TemporaryPasswordExpired/temporary-password-expired'
import EnableMFA from './features/userauth/signup/EnableMFA/enable-mfa'
import EnterMFA from './features/userauth/login/EnterMFA/enter-mfa'
import LoginOutlet from './features/userauth/login/LoginOutlet/login-outlet'
import { routePaths } from './constants/routes'
import SignUpOutlet from './features/userauth/signup/SignUpOutlet/sign-up-outlet'
import RoleManagementOutlet from './features/rolemgmt/RoleManagementOutlet/role-management-outlet'
import RoleManagement from './features/rolemgmt/RoleManagement/role-management'
import UpdateRole from './features/rolemgmt/RoleManagement/components/UpdateRole/UpdateRole'
import PermissionManagement from './features/rolemgmt/PermissionManagement/PermissionManagement'
import UpdatePermission from './features/rolemgmt/PermissionManagement/components/UpdatePermission/UpdatePermission'
import HoldAllocationOrders from './features/holdallocation/HoldAllocationOrders/hold-allocation-orders'
import HoldAllocationListingV2 from './features/holdallocation/HoldAllocationListingV2/hold-allocation-listing-v2'
import HoldAllocationDetail from './features/holdallocation/HoldAllocationDetail/hold-allocation-detail'
import HoldAllocationDetailV2 from './features/holdallocation/HoldAllocationDetailV2/hold-allocation-detail-v2'

Amplify.configure(awsconfig)

function App() {
  const dispatch = useDispatch()

  useEffect(() => {
    /* Get the AWS credentials for the current user from Identity Pools.  */
    try {
      getAuthenticatedUserInfo()
        .then((user) => {
          dispatch(setAuthenticatedUser(user))
        })
        .catch(() => {
          // Nothing to do
        })
    } catch (e) {
      /* Nothing to do here */
    }
  }, [dispatch])

  const appStyle = {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    paddingBottom: '100px',
  }

  return (
    <ErrorBoundary
      FallbackComponent={FallbackComponent}
      onReset={() => {
        // Reset the state of your app so the error doesn't happen again
      }}
    >
      <CssBaseline />
      <Box sx={appStyle}>
        <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <Routes>
            <Route path='/' element={<PrivateRoutes />}>
              {/* Home Page */}
              <Route path='/dashboard' element={<Dashboard />} />
              {/* User Management Pages */}
              <Route path='/user-management' element={<UserManagement />} />
              {/* User Settings */}
              <Route path='/user-settings' element={<AccountSetting />} />
              {/* Orders Pages */}
              <Route path='/orders' name='Orders' element={<Orders />} />
              <Route path='/orderlist' name='OrderList' element={<OrderList />} />
              <Route path='/orderdetails' name='OrderDetails' element={<OrderDetails />} />
              {/* Inventory Pages */}
              <Route path='/inventory' name='Inventory' element={<Inventory />} />
              <Route path='/itemdetails' name='ItemDetails' element={<ItemDetails />} />
              <Route path='/itemcodesummary' name='ItemCodeSummary' element={<ItemCodeSummary />} />
              <Route
                path='/orderallocations'
                name='OrderAllocations'
                element={<OrderAllocations />}
              />
              <Route
                path='/itemspecialpricing'
                name='ItemSpecialPricing'
                element={<ItemSpecialPricing />}
              />
              <Route path='/customergroup' name='CustomerGroup' element={<CustomerGroup />} />
              <Route path='/serialnumbers' name='SerialNumbers' element={<SerialNumbers />} />
              {/* Reports Pages */}
              <Route path='/reports' name='Reports' element={<Reports />} />
              <Route
                path='/displayreports/:reportCategory'
                name='DisplayReports'
                element={<DisplayReports />}
              />
              {/* Invoices Pages */}
              <Route path='/invoices' name='Invoices' element={<Invoices />} />
              <Route path='/creditinvoices' name='CreditInvoices' element={<CreditInvoices />} />
            </Route>
            {/* Login */}
            <Route path={routePaths.LOGIN} name='Login' element={<LoginOutlet />}>
              <Route index name='Login' element={<Login />} />
              <Route path={routePaths.ENTER_MFA} name='EnterMFA' element={<EnterMFA />} />
            </Route>
            {/* Signup Routes */}
            <Route path='/-signup' name='SignUp' element={<SignUpOutlet />}>
              <Route index name='ConfirmInvite' element={<ConfirmInvite />} />
              <Route
                path='/-signup/temporary-password-expired'
                element={<TemporaryPasswordExpired />}
              />
              <Route path='/-signup/profile-setup' name='ProfileSetup' element={<ProfileSetup />} />
              <Route path='/-signup/enable-mfa' element={<EnableMFA />} />
              <Route path='/-signup/success' name='SignUpCompleted' element={<SignUpCompleted />} />
            </Route>
            {/* Reinvite Routes */}
            <Route
              path='/resend-password'
              name='ResendPasswordRoot'
              element={<ResendPasswordOutlet />}
            >
              <Route index name='ResendPassword' element={<ResendPassword />} />
              <Route path='/resend-password/success' element={<ResendPasswordSuccess />} />
            </Route>
            {/* Forgot Password Routes */}
            <Route path='/reset-password' name='ResetPassword' element={<ResetPassword />} />
            <Route
              path='/reset-password-sent'
              name='ResetPasswordSent'
              element={<ResetPasswordSent />}
            />
            <Route
              path='/reset-password-confirm'
              name='SetNewPassword'
              element={<SetNewPassword />}
            />
            <Route
              path='/set-new-password-success'
              name='SetNewPasswordSuccess'
              element={<SetNewPasswordSuccess />}
            />
            {/* Role Management */}
            <Route
              path='/role-management'
              name='RoleManagementRoot'
              element={<RoleManagementOutlet />}
            >
              <Route index name='RoleManagement' element={<RoleManagement />} />
              <Route
                path='/role-management/create-role'
                name='CreateRoleManagement'
                element={<UpdateRole />}
              />
              <Route
                path='/role-management/edit-role'
                name='EditRoleManagement'
                element={<UpdateRole />}
              />
              {/* Permission Management */}
              <Route
                path='/role-management/manage-permissions'
                name='PermissionManagement'
                element={<PermissionManagement />}
              />
              <Route
                path='/role-management/manage-permissions/create-permission'
                name='CreatePermissionManagement'
                element={<UpdatePermission />}
              />
              <Route
                path='/role-management/manage-permissions/edit-permission'
                name='EditPermissionManagement'
                element={<UpdatePermission />}
              />
            </Route>
            {/* Hold & Allocation Demo — no auth required */}
            <Route path='/hold-allocation' name='HoldAllocation' element={<HoldAllocationListingV2 />} />
            <Route path='/hold-allocation/v1' name='HoldAllocationV1' element={<HoldAllocationOrders />} />
            <Route path='/hold-allocation/v1/:orderId' name='HoldAllocationDetailV1' element={<HoldAllocationDetail />} />
            <Route path='/hold-allocation/:orderId' name='HoldAllocationDetail' element={<HoldAllocationDetailV2 />} />
          </Routes>
        </BrowserRouter>
      </Box>
    </ErrorBoundary>
  )
}

export default App
